// Pen-drawn marks for the hero cards: what the hero is (reach, role) and what he brings to a fight.
// Reach and role come from the core stats so they cannot drift; the two traits are authored per hero.
import { HEROES, type HeroKind, type Role } from '../core/heroes.js';
import { t } from './i18n/index.js';
import { roleName } from './i18n/names.js';

export type IconId = 'melee' | 'ranged' | 'guard' | 'shooter' | 'duelist' | 'support' | 'gamechanger' | 'summoner' | 'summon'
  | 'dash' | 'stun' | 'aoe' | 'zone' | 'heal' | 'shield' | 'reveal' | 'control' | 'empower';

/** The mark's caption: a role reads as the role's own name, the rest from icon.* in the dictionary. */
export function iconLabel(id: IconId): string {
  return isRole(id) ? roleName(id) : t(`icon.${id}` as const);
}
const ROLES: readonly Role[] = ['guard', 'shooter', 'duelist', 'support', 'gamechanger', 'summoner'];
const isRole = (id: IconId): id is IconId & Role => (ROLES as readonly string[]).includes(id);

/* 16×16, stroked in the current ink colour: they sit beside engraved text, so no fills. */
const D: Record<IconId, string> = {
  melee: 'M3 13 13 3M11 3h2v2M4.5 11.5 2 14l1.2.3.3 1.2 2.5-2.5',
  ranged: 'M2 14 14 2M14 2h-4M14 2v4M4 9l3 3',
  guard: 'M8 2 3 4v4.5C3 11.5 5.2 13.6 8 14.5c2.8-.9 5-3 5-6V4L8 2z',
  shooter: 'M8 2v3M8 11v3M2 8h3M11 8h3M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  duelist: 'M3 13 12 4M4 3l9 9M3 12v1.5h1.5M12.5 12H14v1.5',
  support: 'M8 3v9M5 6.5h6M4 3h8l-1 10.5H5L4 3z',
  gamechanger: 'M8 2.5 13.5 8 8 13.5 2.5 8 8 2.5zM8 5.5v3M8 10.5v.5',
  summoner: 'M8 2.5v4M5.5 4.5 8 7l2.5-2.5M3 13.5c0-2.8 2.2-5 5-5s5 2.2 5 5',
  summon: 'M3 12.5c1-2 2.5-3 5-3s4 1 5 3M5.5 7a1.2 1.2 0 1 0 0 .1M10.5 7a1.2 1.2 0 1 0 0 .1M8 3.5v1.5',
  dash: 'M2 8h9M8 5l3.5 3L8 11M12.5 4.5 14 8l-1.5 3.5',
  stun: 'M8 3.5 9 6l2.5.5-1.8 1.8.4 2.6L8 9.6l-2.1 1.3.4-2.6L4.5 6.5 7 6l1-2.5M3 12.5l1 1M13 12l-1 1',
  aoe: 'M8 8m-5.5 0a5.5 5.5 0 1 0 11 0 5.5 5.5 0 1 0-11 0M8 8m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0',
  zone: 'M3.5 10c0-2 2-3.5 4.5-3.5S12.5 8 12.5 10 10.5 13 8 13s-4.5-1-4.5-3zM6 4.5l.8 1.4M10 4l-.7 1.6',
  heal: 'M8 3.5v9M3.5 8h9',
  shield: 'M8 2.5 3.5 4.3v4C3.5 11 5.5 13 8 13.8c2.5-.8 4.5-2.8 4.5-5.5v-4L8 2.5zM8 6v4M6 8h4',
  reveal: 'M1.5 8S4 4 8 4s6.5 4 6.5 4-2.5 4-6.5 4-6.5-4-6.5-4zM8 6.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6z',
  control: 'M5 3.5v3M11 3.5v3M3.5 6.5h9v2a4.5 4.5 0 0 1-9 0v-2zM6.5 13.5h3',
  empower: 'M8 13V4M5 7l3-3 3 3M4.5 14.5h7',
};

export function iconSvg(id: IconId, cls = 'hs-ico'): string {
  return `<svg class="${cls}" viewBox="0 0 16 16" width="16" height="16" role="img" aria-label="${iconLabel(id)}">`
    + `<title>${iconLabel(id)}</title><path d="${D[id]}"/></svg>`;
}

/* Two marks per hero: what he actually does in a fight, in the order a picker reads them. */
const TRAITS: Record<HeroKind, [IconId, IconId]> = {
  rhino: ['dash', 'stun'],
  durer: ['control', 'shield'],
  landsknecht: ['aoe', 'empower'],
  arquebusier: ['empower', 'control'],
  astrologer: ['aoe', 'reveal'],
  salamander: ['zone', 'aoe'],
  death: ['dash', 'empower'],
  ferryman: ['control', 'reveal'],
  devil: ['dash', 'aoe'],
  plague: ['heal', 'shield'],
  monk: ['heal', 'control'],
  grotesque: ['zone', 'control'],
  shadow: ['dash', 'empower'],
  sin: ['control', 'shield'],
  nightmare: ['dash', 'control'],
  birdcatcher: ['summon', 'reveal'],
  puppeteer: ['summon', 'control'],
  shewolf: ['summon', 'empower'],
};

export function heroIcons(id: HeroKind): IconId[] {
  const def = HEROES[id];
  return [def.melee ? 'melee' : 'ranged', def.role as IconId, ...TRAITS[id]];
}
